<script setup lang="ts">
import { computed, ref } from "vue";
import { controlClass } from "@/design/control";
import { type PaginationLabels, paginationLabels } from "./pagination";

/**
 * Where you are in a list of things, and how to leave.
 *
 * Design-system primitive: it knows a total, a page size and an offset, and
 * nothing about what is being paged. The screen keeps the offset — a component
 * that remembered it would carry page four into a filter that has three.
 *
 * The RANGE is spelled out rather than a page number, because "26–50 de 4.312"
 * answers the question people actually have ("is there more?") and a page number
 * answers it only after arithmetic.
 *
 * ── HOW MANY PER PAGE ─────────────────────────────────────────────────────
 *
 * Offered only when the screen passes `pageSizes`, and it EMITS rather than
 * decides: a control that nobody is listening to is worse than no control, and
 * this component has no idea what it is paging, so it cannot be the one that
 * remembers the choice. The list that owns the query persists it — per entity,
 * because 100 rows of one thing says nothing about another.
 *
 * ── THE NUMBERS AND THE JUMP ──────────────────────────────────────────────
 *
 * Page 80 used to cost 79 clicks. The numbers and the jump box both emit an
 * OFFSET like everything else here, so a screen that already listens to `update`
 * gains them without changing a line: the page number is arithmetic on `limit`
 * and belongs here, not in every list.
 *
 * ── THE WORDS ─────────────────────────────────────────────────────────────
 *
 * Given by the screen, English by default, and explained in `pagination.ts`.
 */
const props = defineProps<{
	total: number;
	limit: number;
	offset: number;
	/** e.g. `[25, 50, 100]`. Absent means the screen does not offer the choice. */
	pageSizes?: readonly number[];
	alwaysVisible?: boolean;
	/** Only what the screen wants to say differently; the rest stays English. */
	labels?: Partial<PaginationLabels>;
}>();
const emit = defineEmits<{ update: [number]; "update:limit": [number] }>();

const text = computed<PaginationLabels>(() => ({ ...paginationLabels, ...props.labels }));

const first = computed(() => (props.total === 0 ? 0 : props.offset + 1));
const last = computed(() => Math.min(props.offset + props.limit, props.total));
const hasPrevious = computed(() => props.offset > 0);
const hasNext = computed(() => last.value < props.total);

/**
 * Also shown when the page is BIGGER than the smallest size on offer: at 100 per
 * page a list of 40 fits on one page, and hiding the bar there would take away
 * the only way back to 25.
 */
const smallest = computed(() => Math.min(...(props.pageSizes ?? [props.limit])));
const visible = computed(
	() => props.alwaysVisible || props.total > props.limit || props.limit > smallest.value,
);

function go(offset: number): void {
	emit("update", Math.max(0, offset));
}

const size = computed(() => Math.max(1, props.limit));
const pages = computed(() => Math.max(1, Math.ceil(props.total / size.value)));
const current = computed(() =>
	Math.min(pages.value, Math.floor(Math.max(0, props.offset) / size.value) + 1),
);

/**
 * The current page ±2, always with the first and the last, and "…" where the run
 * breaks. A window instead of every number because a list of 4.000 has 160 pages
 * and a bar 160 numbers wide is not a control; the first and the last stay
 * because "voltar ao começo" and "ver o fim" are the two jumps people make.
 */
const numbers = computed<Array<number | "…">>(() => {
	const from = Math.max(1, current.value - 2);
	const to = Math.min(pages.value, current.value + 2);
	const window: Array<number | "…"> = [];
	for (let page = from; page <= to; page += 1) window.push(page);

	const head: Array<number | "…"> = from > 1 ? (from > 2 ? [1, "…"] : [1]) : [];
	const tail: Array<number | "…"> =
		to < pages.value ? (to < pages.value - 1 ? ["…", pages.value] : [pages.value]) : [];
	return [...head, ...window, ...tail];
});

function goToPage(page: number): void {
	const clamped = Math.min(pages.value, Math.max(1, Math.trunc(page)));
	go((clamped - 1) * size.value);
}

/** The box earns its place only where walking stopped being an option. */
const jumpable = computed(() => pages.value > 7);
const jump = ref("");

function submitJump(): void {
	const page = Number(jump.value);
	if (Number.isFinite(page) && page >= 1) goToPage(page);
	// Cleared either way: a number left in the box is a page you are not on.
	jump.value = "";
}
</script>

<template>
	<nav
		v-if="visible"
		class="flex flex-wrap items-center justify-between gap-2 py-2 text-xs text-fg-muted"
		:aria-label="text.nav"
	>
		<p aria-live="polite">{{ first }}–{{ last }} {{ text.of }} {{ total }}</p>
		<div class="flex items-center gap-1">
			<label v-if="pageSizes?.length" class="mr-1 flex items-center gap-1">
				<span class="sr-only sm:not-sr-only">{{ text.perPage }}</span>
				<select
					:class="controlClass('sm', 'w-auto text-xs')"
					:value="limit"
					@change="emit('update:limit', Number(($event.target as HTMLSelectElement).value))"
				>
					<option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
				</select>
			</label>
			<button
				type="button"
				class="h-8 rounded-full border border-border bg-surface-raised px-3 text-fg hover:bg-bg-muted disabled:cursor-not-allowed disabled:opacity-40"
				:disabled="!hasPrevious"
				@click="go(offset - limit)"
			>
				{{ text.previous }}
			</button>
			<!-- On a phone the numbers would wrap onto a second line and push the two
			     buttons off the thumb's reach; the jump box below still gets you there. -->
			<div v-if="pages > 1" class="hidden items-center gap-0.5 sm:flex">
				<template v-for="(page, index) in numbers" :key="`${page}-${index}`">
					<span v-if="page === '…'" class="px-1 text-fg-subtle" aria-hidden="true">…</span>
					<button
						v-else
						type="button"
						class="h-8 min-w-8 rounded-full px-2 tabular-nums"
						:class="
							page === current
								? 'bg-accent-subtle font-medium text-accent'
								: 'text-fg hover:bg-bg-muted'
						"
						:aria-current="page === current ? 'page' : undefined"
						:aria-label="`${text.page} ${page}`"
						@click="goToPage(Number(page))"
					>
						{{ page }}
					</button>
				</template>
			</div>
			<button
				type="button"
				class="h-8 rounded-full border border-border bg-surface-raised px-3 text-fg hover:bg-bg-muted disabled:cursor-not-allowed disabled:opacity-40"
				:disabled="!hasNext"
				@click="go(offset + limit)"
			>
				{{ text.next }}
			</button>
			<label v-if="jumpable" class="ml-1 flex items-center gap-1">
				<span class="sr-only sm:not-sr-only">{{ text.goTo }}</span>
				<input
					v-model="jump"
					type="number"
					min="1"
					:max="pages"
					inputmode="numeric"
					:class="controlClass('sm', 'w-16 text-xs tabular-nums')"
					:aria-label="`${text.goToPage}, 1–${pages}`"
					@keydown.enter.prevent="submitJump"
					@change="submitJump"
				/>
			</label>
		</div>
	</nav>
</template>
